<script setup>
  import {ref} from "vue";

  import ComponentAlert from "@components/Alerts/Alert.vue";
  import {useForm} from "@inertiajs/vue3";
  import PatientinputComponentScannerDialog from "@pages/Patientinput/Components/Scanners/ScannerDialog.vue";
  import {circumstance} from "@pages/Records/Components/Sections/MedicationBar/Components/Indications/enum.js";
  import {MedButton, MedSpinner} from "@pharma4u/medicheck-components";
  import Bmp from "@utils/Bmp.js";
  import {coreRouter} from "@utils/coreRouter/coreRouter.js";
  import {pdfRouter} from "@utils/pdfRouter/pdfRouter.js";
  import {Symbology} from "scandit-web-datacapture-barcode";

  defineOptions({
    name: "PatientinputComponentBmpScannerDialog",
  });

  const props = defineProps({
    form: {
      type: Object,
      required: true,
    },
  });

  const emit = defineEmits(["ready"]);

  const refScannerDialog = ref(null);

  const currentScannedPage = ref(0);
  const error = ref(null);
  const formats = [Symbology.DataMatrix];
  const nextPage = ref(0);
  const pagesScanned = ref([]);
  const paused = ref(false);
  const totalPages = ref(0);

  let uniqueInstanceId = null;

  const bmpForm = useForm({
    pages: [],
  });

  const open = () => {
    refScannerDialog.value.open();
  };

  const close = () => {
    refScannerDialog.value.close();
  };

  const onDetect = (detectedCodes) => {
    paused.value = true;

    if (!Bmp.validateXml(detectedCodes[0]._data)) {
      error.value = "Es wurde kein gültiger Medikationsplan erkannt.";
      return;
    }

    error.value = null;

    const xml = detectedCodes[0]._data;

    const currentPageNumber = Bmp.getCurrentPageNumber(xml);
    const totalPageNumber = Bmp.getTotalPageNumber(xml);
    const scannedUniqueInstanceId = Bmp.getUniqueInstanceId(xml);

    if (uniqueInstanceId && uniqueInstanceId !== scannedUniqueInstanceId) {
      error.value = "Bitte scannen Sie nur Seiten des gleichen Medikationsplans.";
    } else if (pagesScanned.value.includes(currentPageNumber)) {
      error.value = "Sie haben diese Seite bereits gescannt.";
    } else if (pagesScanned.value.length <= totalPageNumber) {
      currentScannedPage.value = currentPageNumber;
      totalPages.value = totalPageNumber;

      pagesScanned.value.push(currentPageNumber);

      bmpForm.pages.push(xml);

      uniqueInstanceId = scannedUniqueInstanceId;

      if (pagesScanned.value.length === totalPageNumber) {
        parseXml();
      } else if (pagesScanned.value.length < totalPageNumber) {
        if (!pagesScanned.value.includes(1)) {
          nextPage.value = 1;
        } else if (!pagesScanned.value.includes(2)) {
          nextPage.value = 2;
        } else if (!pagesScanned.value.includes(3)) {
          nextPage.value = 3;
        }
      }
    } else {
      error.value =
        "Es ist ein unbekannter Fehler aufgetreten. Bitte klicken Sie auf „Abbrechen“ und starten den Scan erneut.";
    }
  };

  const parseXml = () => {
    bmpForm.processing = true;

    new Promise((resolve, reject) => {
      pdfRouter.patientinputDecodeXml(bmpForm.data(), {
        onSuccess: (res) => {
          const parsedData = res.data;

          ["firstname", "lastname", "birthdate", "gender", "insurancenumber"].forEach((key) => {
            if (props.form.masterdata.patient[key] === null) {
              props.form.masterdata.patient[key] = parsedData.patient[key];
            }
          });

          props.form.medication.pzns.push(...new Set(parsedData.pzns)); // Set to avoid duplicates
          props.form.medication.bmp = res.data_raw;

          ["size", "weight"].forEach((key) => {
            if (props.form.laborvalues[key] === null) {
              props.form.laborvalues[key] = parsedData.laborvalues[key] ?? null;
            }
          });

          // lactating / pregnant woman when on patient input form
          if (
            props.form.meta.is_patient &&
            (props.form.masterdata.patient.gender === "female" || props.form.masterdata.patient.gender === "diverse")
          ) {
            if (parsedData?.circumstances?.lactatingWoman) {
              props.form.circumstances.mivs.push(circumstance.LACTATING_WOMAN);
            }
            if (parsedData?.circumstances?.pregnantWoman) {
              props.form.circumstances.mivs.push(circumstance.PREGNANT_WOMAN);
            }
          }

          resolve();
        },
        onError: reject,
      });
    })
      .then(async () => {
        await new Promise((resolve, reject) => {
          coreRouter.searchPacForPatientinput(props.form.medication.pzns.join(","), {
            onSuccess: resolve,
            onError: reject,
          });
        });
      })
      .then(() => {
        bmpForm.processing = false;
        emit("ready");
      })
      .catch(() => {
        error.value = "Beim Verarbeiten gab es einen Fehler. Bitte versuchen Sie es erneut.";
        bmpForm.pages = [];
        totalPages.value = 0;
        nextPage.value = 0;
        pagesScanned.value = [];
        currentScannedPage.value = 0;
        bmpForm.processing = false;
      });
  };

  defineExpose({
    open,
    close,
  });
</script>

<template>
  <PatientinputComponentScannerDialog
    ref="refScannerDialog"
    dialog-title="Medikationsplan (BMP) scannen"
    dialog-type="abort"
    :formats="formats"
    loader-class="aspect-square"
    scanner-class="aspect-square"
    :primary-button-disabled="totalPages === 0 || totalPages !== pagesScanned.length"
    :paused="paused"
    :test-id="getTestId('scanner')"
    @detect="onDetect"
  >
    <p v-if="pagesScanned.length === 0 && !error">
      Bitte scannen Sie den Datamatrix-Code von Ihrem bundeseinheitlichen Medikationsplan (BMP).
    </p>

    <p v-else-if="totalPages !== pagesScanned.length && !paused">
      Bitte Seite {{ nextPage }} von {{ totalPages }} scannen.
    </p>

    <template #paused>
      <ComponentAlert v-if="error" type="error" class="mb-0! w-full" :test-id="getTestId('scanner-dialog')">
        <p>{{ error }}</p>

        <MedButton
          variant="primary"
          class="mt-2"
          :data-test="getTestId('scanner-dialog-continue-scan-button')"
          @click="paused = false"
        >
          Weiterscannen
        </MedButton>
      </ComponentAlert>

      <template v-else-if="bmpForm.processing">
        <MedSpinner size="3xl" :data-test="getTestId('bmp-scanner-dialog-spinner')" />

        <ComponentAlert type="succes" class="mb-0! w-full" :test-id="getTestId('scanner-dialog-processing')">
          Der gescannte Medikationsplan wird verarbeitet. Bitte haben Sie einen kurzen Augenblick Geduld.
        </ComponentAlert>
      </template>

      <ComponentAlert v-else type="succes" class="mb-0! w-full" :test-id="getTestId('scanner-dialog')">
        <p>Seite {{ currentScannedPage }} von {{ totalPages }} wurde gescannt.</p>

        <MedButton
          class="mt-2"
          :data-test="getTestId('scanner-dialog-continue-scan-button')"
          variant="primary"
          @click="paused = false"
        >
          Weiterscannen
        </MedButton>
      </ComponentAlert>
    </template>
  </PatientinputComponentScannerDialog>
</template>
